import type { DeepExplorerSessionState } from "./deep-explorer-session-state"
import type {
  ChatAppResponse,
  ChatSessionModel,
  DurableChatTurnIdentity,
  ToolApproval,
  UserDocument,
} from "@/api/models"
import type { SteerNote } from "@/features/chat/types/steer-types"
import type { StateCreator } from "zustand"

export type ChatInteraction = [question: string, response: ChatAppResponse | undefined]

/** Cursor-style message queue entry (#2437). Stable `id` for React keys / remove. */
export type QueuedMessage = {
  id: string
  text: string
}

export type SessionState = {
  messages: [user: string, ChatAppResponse][] // Completed, saved answers
  streamingAnswer: [user: string, ChatAppResponse] | null // Current in-progress answer
  isLoading: boolean
  isHydrating: boolean // Loading chat history from server (not waiting for AI answer)
  isStreaming: boolean
  error?: unknown
  /**
   * Machine-readable companion of `error` (`ChatAppResponse.error_code`).
   * Explorer drops an empty streaming bubble when `error` is set (#2098) and
   * renders `<Chat.Error error={chatState.error}>`, so the code cannot live
   * only on `streamingAnswer[1]`. `"fair_use_blocked"` selects the in-chat
   * budget gate instead of the destructive Retry/Report card.
   */
  errorCode?: string
  lastQuestion: [string, File[]]
  lastRequestAgentId?: string
  tokenCounts: {
    files: number
    text: number
  }
  canContinue: boolean
  isAwaitingSave: boolean
  queueRequestId: string | null
  toolApprovals: ToolApproval[]
  toolApprovalSessionId: string | null
  /** Cursor-style message queue (admin-gated, #2437): messages typed and sent
   *  while a turn is running, held here in arrival order until the turn
   *  settles. Session-memory only — not persisted across reloads. */
  queuedMessages: QueuedMessage[]
  /** AI Explorer-only — per-session Deep Explorer run state. Optional because
   *  other modules (Video Insight, Translator, Dash) share `SessionState` but
   *  don't run Deep. Read via `getDeepStateForSession()` for safe defaults. */
  deepExplorerState?: DeepExplorerSessionState
  // ── Explorer steering (issue #2438) — all inert defaults when the
  //    `explorer_steer` flag is off; only the Explorer main-chat WS ever
  //    flips them. ──────────────────────────────────────────────────────────
  /** Server emitted `steer_ready` for the running turn (tool loop is live).
   *  Cleared when a turn starts or ends. */
  steerReady: boolean
  /** Steering-note chips for the running turn; cleared when the turn ends. */
  steerNotes: SteerNote[]
  /** Next steer-note sequence for a DURABLE turn (#2814), scoped to the turn it
   *  was minted for. The server derives the control id from this sequence, so
   *  two tabs racing on one value collide on the id and the loser is refused
   *  rather than silently sharing a slot. Carrying the turn id makes it
   *  self-resetting — a new turn starts at 1 with no separate reset step. Null
   *  until this session posts its first durable note. */
  durableSteerSeq: { turnId: string; next: number } | null
  /** A `{"type":"cancel"}` frame is in flight for the running turn. */
  cancelPending: boolean
  /** Server acknowledged the cancel frame (`cancel_ack`); the client now waits
   *  through the server's save budget instead of hard-aborting. */
  cancelAckReceived: boolean
  /** Composer text stashed on Stop, auto-sent as a new turn once the cancelled
   *  turn is terminal AND its partial is persisted (stop-and-redirect).
   *  `armedAt` (epoch ms) lets the auto-send effect drop stale redirects when
   *  a session is revisited long after the cancel.
   *
   *  `viaDurableTurn` marks a redirect armed against a DURABLE turn (issue
   *  #2813). Those never receive a `message_saved` ack — the durable runner
   *  owns persistence and `_translate_stream_frame` drops that legacy frame
   *  ("the durable runner owns persistence") — so the auto-send effect waits
   *  on the turn settling (`durableTurn` cleared by the registry mirror once
   *  the canonical terminal status arrives, which is emitted only after the
   *  partial is persisted) instead of on `sawMessageSaved`. */
  pendingRedirect: { text: string; armedAt: number; viaDurableTurn?: boolean } | null
  /** True once a real `message_saved` ack arrived for the running turn. The
   *  stop-and-redirect auto-send requires this: `isAwaitingSave` can also be
   *  released by the save-guard watchdog when persistence actually failed, and
   *  a redirect must not fire on top of an unsaved turn. Reset at turn start. */
  sawMessageSaved: boolean
  // ── Steer markers (issue #2468 / #2469) — best-effort, frontend-only.
  //    Queued drains and force redirects render as normal primary user
  //    bubbles (same as a typed send). Only steered notes use the compact
  //    provenance pill, as EXTRA markers alongside a real turn — never in
  //    place of the turn's user message. Session-memory only. ─────────────
  /** Fallback FIFO for delivered steer text when no live turn index exists
   *  yet (issue #2469 prefers attaching straight to `messageSteerMarkers`
   *  on deliver). Drained by the settle effect in use-ai-explorer-chat.ts. */
  pendingSteerMarkerQueue: string[]
  /** Maps a message / live-stream index to steer note(s) delivered during
   *  that turn. Rendered as "Steered" marker bubbles; populated immediately
   *  on `steer_status: delivered` when a live index is known (#2469). */
  messageSteerMarkers: Record<number, string[]>
  /** Identity of the durable turn currently bound to this session's live UI
   *  state (issue #2465), or `null` when none is active. Set once HTTP
   *  acceptance returns and cleared once the turn reaches a terminal status.
   *  Exact Stop and durable tool-approval controls capture this at click
   *  time so a click always binds the turn that was actually running, never
   *  whatever turn is active by the time the click's request lands. Always
   *  `null` when the durable gate is off — inert for every other mode. */
  durableTurn: DurableChatTurnIdentity | null
  /** Mirrors `DurableChatTurnRegistryState.reconnecting` for `durableTurn`
   *  (issue #2513 finding 5): true while the turn's transport is trying to
   *  recover (WebSocket reconnect backoff or HTTP status-poll fallback)
   *  rather than actively confirmed live. Lets a bounded backstop
   *  (`useSaveGuardWatchdog`) release a viewer that would otherwise stay
   *  stuck indefinitely if recovery itself never settles. Always `false`
   *  when `durableTurn` is `null`. */
  durableTurnReconnecting: boolean
}

type FileSlice = {
  files: File[]
  addFiles: (files: File[]) => void
  removeFile: (image: File) => void
  resetFiles: () => void
  selectedDocuments: UserDocument[]
  addDocument: (document: UserDocument) => void
  removeDocument: (documentId: string) => void
  resetDocuments: () => void
  // Persisted file references from session (filenames only, for display)
  sessionFileRefs: string[]
  setSessionFileRefs: (refs: string[]) => void
}

export type ChatSlice = FileSlice & {
  sessionStates: Record<string, SessionState>
  sessions: ChatSessionModel[]
  unreadResponseSessionIds: string[]
  currentSessionId: string | null
  changeTokenCounts: (tokenCounts: Partial<SessionState["tokenCounts"]>) => void
  setCurrentSessionId: (sessionId: string | null) => void
  setSessions: (sessions: ChatSessionModel[]) => void
  markSessionResponseUnread: (sessionId: string) => void
  clearSessionResponseUnread: (sessionId: string) => void
  resetChatState: () => void
  changeChatState: (chatState: Partial<SessionState>) => void
  initSession: (sessionId: string) => void
  removeSessionState: (sessionId: string) => void
  updateSession: (sessionId: string, partialState: Partial<SessionState>) => void
}

export const defaultSessionState: SessionState = {
  messages: [],
  streamingAnswer: null,
  isLoading: false,
  isHydrating: false,
  isStreaming: false,
  error: undefined,
  errorCode: undefined,
  lastQuestion: ["", []],
  lastRequestAgentId: undefined,
  tokenCounts: {
    files: 0,
    text: 0,
  },
  canContinue: false,
  isAwaitingSave: false,
  queueRequestId: null,
  toolApprovals: [],
  toolApprovalSessionId: null,
  queuedMessages: [],
  steerReady: false,
  steerNotes: [],
  durableSteerSeq: null,
  cancelPending: false,
  cancelAckReceived: false,
  pendingRedirect: null,
  sawMessageSaved: false,
  pendingSteerMarkerQueue: [],
  messageSteerMarkers: {},
  durableTurn: null,
  durableTurnReconnecting: false,
}

export const createChatSlice: StateCreator<ChatSlice> = (set, get) => ({
  files: [],
  addFiles(files) {
    set({ files: [...get().files, ...files] })
  },
  removeFile(fileToRemove) {
    set({ files: get().files.filter((file) => file !== fileToRemove) })
  },
  resetFiles() {
    set({ files: [], sessionFileRefs: [] })
  },
  selectedDocuments: [],
  addDocument(document) {
    set((state) => {
      const alreadyExists = state.selectedDocuments.some(
        (selectedDocument) => selectedDocument.id === document.id
      )
      if (alreadyExists) {
        return state
      }
      return { selectedDocuments: [...state.selectedDocuments, document] }
    })
  },
  removeDocument(documentId) {
    set((state) => ({
      selectedDocuments: state.selectedDocuments.filter((document) => document.id !== documentId),
    }))
  },
  resetDocuments() {
    set({ selectedDocuments: [] })
  },
  sessionFileRefs: [],
  setSessionFileRefs(refs) {
    set({ sessionFileRefs: refs })
  },
  changeTokenCounts(tokenCounts) {
    const { currentSessionId } = get()
    if (!currentSessionId) return

    const sessionState = get().sessionStates[currentSessionId] ?? { ...defaultSessionState }
    get().updateSession(currentSessionId, {
      tokenCounts: { ...sessionState.tokenCounts, ...tokenCounts },
    })
  },
  sessions: [],
  unreadResponseSessionIds: [],
  currentSessionId: null,
  setSessions(sessions) {
    set({ sessions })
  },
  setCurrentSessionId(sessionId) {
    set((state) => {
      const unreadResponseSessionIds =
        sessionId && state.unreadResponseSessionIds.includes(sessionId)
          ? state.unreadResponseSessionIds.filter((id) => id !== sessionId)
          : state.unreadResponseSessionIds
      if (
        state.currentSessionId === sessionId &&
        unreadResponseSessionIds === state.unreadResponseSessionIds
      ) {
        return state
      }
      return { currentSessionId: sessionId, unreadResponseSessionIds }
    })
  },
  markSessionResponseUnread(sessionId) {
    set((state) => {
      if (state.unreadResponseSessionIds.includes(sessionId)) return state
      return { unreadResponseSessionIds: [...state.unreadResponseSessionIds, sessionId] }
    })
  },
  clearSessionResponseUnread(sessionId) {
    set((state) => ({
      unreadResponseSessionIds: state.unreadResponseSessionIds.filter((id) => id !== sessionId),
    }))
  },
  resetChatState() {
    const { currentSessionId } = get()
    if (!currentSessionId) return

    get().updateSession(currentSessionId, { ...defaultSessionState })
  },
  changeChatState(chatState) {
    const { currentSessionId } = get()
    if (!currentSessionId) return

    get().updateSession(currentSessionId, chatState)
  },
  sessionStates: {},
  initSession(sessionId) {
    const { sessionStates } = get()
    if (sessionId in sessionStates) {
      return
    }

    set({
      sessionStates: {
        ...sessionStates,
        [sessionId]: { ...defaultSessionState },
      },
    })
  },
  removeSessionState(sessionId) {
    set((state) => {
      const unreadResponseSessionIds = state.unreadResponseSessionIds.filter(
        (id) => id !== sessionId
      )
      if (!(sessionId in state.sessionStates)) {
        return {
          ...(state.currentSessionId === sessionId ? { currentSessionId: null } : {}),
          unreadResponseSessionIds,
        }
      }

      const sessionStates = { ...state.sessionStates }
      delete sessionStates[sessionId]
      return {
        sessionStates,
        unreadResponseSessionIds,
        ...(state.currentSessionId === sessionId ? { currentSessionId: null } : {}),
      }
    })
  },
  updateSession(sessionId, partialState) {
    const { sessionStates } = get()
    const currentState = sessionStates[sessionId] ?? { ...defaultSessionState }
    // `errorCode` is the machine-readable companion of `error`. A patch that
    // writes `error` without an explicit code must not keep a stale one — a
    // later generic failure would otherwise render the fair-use gate.
    const nextPartial: Partial<SessionState> =
      "error" in partialState && !("errorCode" in partialState)
        ? { ...partialState, errorCode: undefined }
        : partialState
    const newState = { ...currentState, ...nextPartial }

    set({
      sessionStates: {
        ...sessionStates,
        [sessionId]: newState,
      },
    })
  },
})
